import { plainToInstance, Transform, Type } from 'class-transformer'
import { cloneDeep } from 'lodash'
import Vue from 'vue'
import i18n from 'vue-i18n'

import { ChannelInfo } from '../../../../../../../../models/CheckChannels/CheckChannels'
import MessageEditorWithMediaData
  from '../../../../../../../NewMessageEditors/NewMessageEditorWithMedia/types'
import { MenuSectionNames, SocketNames } from '../../../../../components'
import { BaseNodeModelData } from '../../../BaseNode/BaseNodeDataModel'
import { BaseNodeModel } from '../../../BaseNode/BaseNodeModel'
import CustomNode from '../../../CustomNode'
import { CustomNodeComponent, NodeTypes } from '../../../index'
import SampleForwardMessageNode from '../ui/SampleNode/SampleForwardMessageNode.vue'
import ForwardMessageSidebar from '../ui/Sidebar/ForwardMessageSidebar.vue'
import { ForwardMessageNodeProps, SendMessageMode } from './types'

export default class ForwardMessageNodeBuilder extends CustomNodeComponent {
  menuSection = MenuSectionNames.Communications
  sidebarComponent = ForwardMessageSidebar

  constructor(i18n: i18n, nodeProps: ForwardMessageNodeProps) {
    super(SampleForwardMessageNode, ForwardMessageNode, NodeTypes.ForwardMessage, i18n, nodeProps)
  }

  getBaseNodeModel = ([ x, y ]: [number, number]) => {
    return {
      data: plainToInstance(ForwardMessageNodeData, {
        type: NodeTypes.ForwardMessage,
        targets: [],
        min_length: 0,
        max_length: 0,
        fail_try_max_count: 0,
        fail_output: CustomNode.LastSocketId += 1,
        fail_message: [],
        mode: SendMessageMode.Forward
      }),
      output: CustomNode.LastSocketId += 1,
      input: CustomNode.LastSocketId += 1,
      color: null,
      x,
      y,
    }
  }
}

export class ForwardMessageNode extends CustomNode {
  data: ForwardMessageNodeData

  declare nodeProps: ForwardMessageNodeProps

  constructor(name: NodeTypes.ForwardMessage, model: BaseNodeModel, nodeProps: ForwardMessageNodeProps) {
    super(name, model, nodeProps)

    if (model.data instanceof ForwardMessageNodeData) {
      this.data = model.data

      this.socketActions.createOutput(model.data.fail_output, SocketNames.Fail)
    } else {
      throw new Error('To node come unknown data')
    }
  }

  hasTarget(target?: string | number) {
    if (target) {
      return this.data.targets.includes(+target)
    }

    return false
  }

  setFailMessage(messages: Array<MessageEditorWithMediaData>): void {
    this.data.fail_message = messages
  }

  setFailTryMaxCount(count: number): void {
    this.data.fail_try_max_count = count
  }

  setMode(mode: SendMessageMode): void {
    this.data.mode = mode
  }

  setTargets(targets: Array<number>): void {
    this.data.targets = targets
  }

  setMinLength(val: number): void {
    this.data.min_length = val
  }

  setMaxLength(val: number): void {
    this.data.max_length = val
  }

  setPattern(pattern: string): void {
    if (this.data.regex) {
      this.data.regex.pattern = pattern
    }
  }

  setInvert(val: boolean): void {
    if (this.data.regex) {
      this.data.regex.invert = val
    }
  }

  get hasRegex() {
    return !!this.data.regex
  }

  toggleRegex(val: boolean, vm: Vue) {
    if (val) {
      vm.$set(this.data, 'regex', {
        pattern: '',
        invert: false,
      })
    } else {
      this.data.regex = null
    }
  }

  removeTarget(id?: string | number) {
    if (id) {
      this.data.targets = this.data.targets.filter(t => t !== id)
    }
  }
}

export class ForwardMessageNodeData extends BaseNodeModelData {
  declare type: NodeTypes.ForwardMessage

  /**
   * Отрицательные числа это каналы или чаты, положительные это пользователи
   * В ноде реализованна пересылка только админам.
   * Не помню точно, почему так сделано, (мб уже может) но по идее в будущем она сможет пересылать сообщения в каналы и чаты.
   */
  targets!: Array<number>
  min_length!: number
  max_length!: number
  fail_try_max_count!: number
  fail_output!: number
  mode!: SendMessageMode

  @Transform(({ value }) => {
    if (value) {
      return value
    }
    return null
  })
  regex?: {
    pattern: string
    invert: boolean
  } | null

  @Type(() => MessageEditorWithMediaData)
  fail_message!: Array<MessageEditorWithMediaData>

  getData(): ForwardMessageNodeData {
    const data = cloneDeep(this)

    if ((data.regex && !data.regex.pattern.trim().length) || data.regex === null) {
      delete data.regex
    }

    return data
  }
}
